import { ArrayComparisonConfig } from '../classes/array-comparison-task/array-comparison-config';
import { CustomLogConfig } from '../classes/custom-log-task/custom-log-config';
import { ExecuteFlowConfig } from '../classes/execute-flow/execute-flow-config';
import { TaskMapping } from '../classes/flow-config/task-mapping';
import { Guid } from '../classes/guid';
import { IfGateConfig } from '../classes/if-gate/if-gate-config';
import { LoopConfig } from '../classes/loop-task/loop-config';
import { MicroTaskConfig } from '../classes/micro-task/micro-task-config';
import { ParallelSplitConfig } from '../classes/parallel-gateways/parallel-split-config';
import { MicrotaskType } from '../enums/microtask-types.enum';
import { ObjectState } from '../enums/objectState.enum';
import { makeExecuteFlowTaskUnique } from './make-execute-flow-task-unique';

function findMappedIdAndReplace(idToMapFrom: string, mappedIds: Map<string, string>) {
	return mappedIds.get(idToMapFrom) ?? idToMapFrom;
}

/**
 * Creates a unique `ExecuteFlowConfig` from the given task mappings and sub-task configs,
 * reassigning new GUIDs to all task mappings and updating all mapping ID references
 * (success/failure/loop/remaining destination) throughout the config tree.
 *
 * Deep-clones both `flowTaskMappings` and `tasks` before processing to avoid
 * mutating the original inputs.
 *
 * Handles the following sub-task types with their specific mapping fields:
 * - `IfGate`: Updates `successMappingID` and `failureMappingID` on each rule group.
 * - `Loop`: Updates `loopDestinationID`.
 * - `ParallelSplit`: Updates `successMappingID` and `failureMappingID` on each split mapping.
 * - `ArrayComparison`: Updates `remainingDestinationID`.
 * - `CustomLog`: Updates `successMappingID` and `failureMappingID` on each message handler.
 *
 * @param flowTaskMappings - The `TaskMapping[]` array to be deep-cloned and uniquified.
 * @param tasks - The `MicroTaskConfig[]` array to be deep-cloned as sub-task configs.
 * @returns A new `ExecuteFlowConfig` with uniquified task mappings and updated ending references.
 */
export function makeFlattenedFlowMappingsUnique(
	flowTaskMappings: TaskMapping[],
	tasks: MicroTaskConfig[]
): ExecuteFlowConfig {
	const executeFlowConfig = makeExecuteFlowTaskUnique(
		MicrotaskType.ExecuteFlow,
		new ExecuteFlowConfig({
			taskMappings: JSON.parse(JSON.stringify(flowTaskMappings)),
			subTaskConfigs: JSON.parse(JSON.stringify(tasks)),
		}),
		ObjectState.Created
	) as ExecuteFlowConfig;

	const mappingIds = new Map<string, string>();

	for (const taskMapping of executeFlowConfig.taskMappings) {
		const initialId = taskMapping.id;
		const newAssignedId = Guid();

		mappingIds.set(initialId, newAssignedId);
		taskMapping.id = newAssignedId;
	}

	executeFlowConfig.successMappingID = findMappedIdAndReplace(
		executeFlowConfig.successMappingID,
		mappingIds
	);
	executeFlowConfig.failureMappingID = findMappedIdAndReplace(
		executeFlowConfig.failureMappingID,
		mappingIds
	);

	for (const subTaskConfig of executeFlowConfig.subTaskConfigs) {
		if (subTaskConfig.config) {
			subTaskConfig.config.successMappingID = findMappedIdAndReplace(
				subTaskConfig.config.successMappingID,
				mappingIds
			);
			subTaskConfig.config.failureMappingID = findMappedIdAndReplace(
				subTaskConfig.config.failureMappingID,
				mappingIds
			);
			switch (subTaskConfig.taskType) {
				case MicrotaskType.IfGate: {
					const subIfGateConfig = subTaskConfig.config as IfGateConfig;
					for (const ifRuleGroup of subIfGateConfig.rules) {
						ifRuleGroup.successMappingID = findMappedIdAndReplace(
							ifRuleGroup.successMappingID,
							mappingIds
						);
						ifRuleGroup.failureMappingID = findMappedIdAndReplace(
							ifRuleGroup.failureMappingID,
							mappingIds
						);
					}
					break;
				}
				case MicrotaskType.Loop: {
					const subLoopTaskConfig = subTaskConfig.config as LoopConfig;
					subLoopTaskConfig.loopDestinationID = findMappedIdAndReplace(
						subLoopTaskConfig.loopDestinationID,
						mappingIds
					);
					break;
				}
				case MicrotaskType.ParallelSplit: {
					const subParallelSplitConfig = subTaskConfig.config as ParallelSplitConfig;
					for (const split of subParallelSplitConfig.parallelSplitMappings) {
						split.successMappingID = findMappedIdAndReplace(split.successMappingID, mappingIds);
						split.failureMappingID = findMappedIdAndReplace(split.failureMappingID, mappingIds);
					}
					break;
				}
				case MicrotaskType.ArrayComparison: {
					const subArrayComparisonTask = subTaskConfig.config as ArrayComparisonConfig;
					subArrayComparisonTask.remainingDestinationID = findMappedIdAndReplace(
						subArrayComparisonTask.remainingDestinationID,
						mappingIds
					);
					break;
				}
				case MicrotaskType.CustomLog: {
					const subCustomLogTask = subTaskConfig.config as CustomLogConfig;
					for (const logItem of subCustomLogTask.messageHandlers) {
						logItem.successMappingID = findMappedIdAndReplace(logItem.successMappingID, mappingIds);
						logItem.failureMappingID = findMappedIdAndReplace(logItem.failureMappingID, mappingIds);
					}
					break;
				}
			}
		}
	}

	return executeFlowConfig;
}
